import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export default function MenuMarginPage() {
  const { user } = useAuth();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/recipes");
        setRows(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const withMargin = useMemo(() => {
    return rows
      .map((r) => {
        const sale = Number(r.sale_price || 0);
        const cost = Number(r.cost_per_serving || 0);
        const profit = sale > 0 ? sale - cost : null;
        const marginPct = sale > 0 ? ((sale - cost) / sale) * 100 : null;
        return { ...r, _profit: profit, _margin: marginPct };
      })
      .filter((r) => (filter === "all" ? true : r.category === filter))
      .sort((a, b) => (b._margin ?? -Infinity) - (a._margin ?? -Infinity));
  }, [rows, filter]);

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const priced = withMargin.filter((r) => r._margin !== null);
  const avgMargin = priced.length
    ? priced.reduce((s, r) => s + r._margin, 0) / priced.length
    : 0;
  const best = priced[0];
  const worst = priced[priced.length - 1];
  const categories = Array.from(new Set(rows.map((r) => r.category)));

  return (
    <div data-testid="menu-margin-page">
      <div className="fade-up">
        <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground">Owner analytics</p>
        <h1 className="font-serif text-3xl sm:text-5xl tracking-tight mt-2">Menu margin report</h1>
        <p className="mt-3 text-muted-foreground">
          Every dish ranked by profit margin. Push the top rows; reprice or replace the bottom rows.
        </p>
      </div>

      {/* Summary */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-primary text-primary-foreground p-6 fade-up" data-testid="mm-avg">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest opacity-70"><Minus className="w-4 h-4" aria-hidden="true" /> Avg margin</div>
          <div className="mt-3 font-serif text-4xl">{priced.length ? `${avgMargin.toFixed(1)}%` : "—"}</div>
          <div className="mt-1 text-xs opacity-70">Across {priced.length} priced dishes</div>
        </div>
        <div className="bg-white border border-border p-6 fade-up" data-testid="mm-best">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-700"><TrendingUp className="w-4 h-4" aria-hidden="true" /> Star dish</div>
          <div className="mt-3 font-serif text-2xl">{best ? best.name : "—"}</div>
          <div className="mt-1 text-sm text-muted-foreground">
            {best ? `${best._margin.toFixed(1)}% margin · profit ${fmt(best._profit)}` : "No priced recipes yet"}
          </div>
        </div>
        <div className="bg-white border border-border p-6 fade-up" data-testid="mm-worst">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-red-700"><TrendingDown className="w-4 h-4" aria-hidden="true" /> Weakest</div>
          <div className="mt-3 font-serif text-2xl">{worst && worst !== best ? worst.name : "—"}</div>
          <div className="mt-1 text-sm text-muted-foreground">
            {worst && worst !== best ? `${worst._margin.toFixed(1)}% margin · profit ${fmt(worst._profit)}` : "Needs at least 2 priced recipes"}
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="mt-8 flex items-center gap-3">
        <Label className="text-xs uppercase tracking-widest text-muted-foreground">Category</Label>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-52" data-testid="mm-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="mt-6 bg-white border border-border overflow-x-auto">
        {loading ? (
          <div className="p-10 text-center text-muted-foreground">Loading…</div>
        ) : withMargin.length === 0 ? (
          <div className="p-14 text-center">
            <p className="font-serif text-2xl">No recipes yet.</p>
            <p className="text-muted-foreground mt-2">Ask the head chef to file a few dishes first.</p>
          </div>
        ) : (
          <table className="w-full min-w-[720px]" data-testid="mm-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
                <th className="px-6 py-3 font-normal">#</th>
                <th className="px-6 py-3 font-normal">Dish</th>
                <th className="px-6 py-3 font-normal">Category</th>
                <th className="px-6 py-3 font-normal text-right">Cost / serving</th>
                <th className="px-6 py-3 font-normal text-right">Sale price</th>
                <th className="px-6 py-3 font-normal text-right">Profit</th>
                <th className="px-6 py-3 font-normal text-right">Margin</th>
              </tr>
            </thead>
            <tbody>
              {withMargin.map((r, idx) => {
                const good = r._margin != null && r._margin >= 50;
                const bad = r._margin != null && r._margin < 25;
                return (
                  <tr key={r.id} className="border-t border-border" data-testid={`mm-row-${r.id}`}>
                    <td className="px-6 py-3 text-muted-foreground">{idx + 1}</td>
                    <td className="px-6 py-3 font-medium">{r.name}</td>
                    <td className="px-6 py-3 text-muted-foreground">{r.category}</td>
                    <td className="px-6 py-3 text-right text-muted-foreground">{fmt(r.cost_per_serving)}</td>
                    <td className="px-6 py-3 text-right">{r.sale_price ? fmt(r.sale_price) : <span className="text-muted-foreground italic">not priced</span>}</td>
                    <td className="px-6 py-3 text-right font-serif">
                      {r._profit == null ? "—" : fmt(r._profit)}
                    </td>
                    <td className="px-6 py-3 text-right">
                      {r._margin == null ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-sm font-serif ${
                          good ? "bg-emerald-100 text-emerald-800" : bad ? "bg-red-100 text-red-800" : "bg-secondary"
                        }`}>
                          {r._margin.toFixed(1)}%
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
